import type {
  EncounterScrubberReview,
  NoteMetadataBillingCode,
  NoteMetadataSnapshot,
} from '@/schemas/note-metadata.schema';
import type { BillingServiceTypeRef } from '@/pages/appointments/noteMetadataMapping';
import type { EncounterScrubberResult } from './types';
import { scrubberResultRowKey, type ScrubberSuggestionDecision } from './scrubberReview';

function normalizeCpt(code: string | undefined): string {
  return (code ?? '').trim().toUpperCase();
}

function defaultBillingRow(): NoteMetadataBillingCode {
  return { code: 'unspecified', units: 1 };
}

function billingRows(metadata: NoteMetadataSnapshot): NoteMetadataBillingCode[] {
  const rows = metadata.billingCodes ?? [];
  return rows.length > 0 ? rows.map((row) => ({ ...row })) : [defaultBillingRow()];
}

function resolveServiceTypeForCpt(
  cptCode: string,
  serviceTypes: BillingServiceTypeRef[],
): BillingServiceTypeRef | undefined {
  const normalized = normalizeCpt(cptCode);
  return serviceTypes.find((st) => normalizeCpt(st.cptCode) === normalized);
}

function rowWithCpt(
  row: NoteMetadataBillingCode,
  cptCode: string,
  serviceTypes: BillingServiceTypeRef[],
  units?: number,
): NoteMetadataBillingCode {
  const serviceType = resolveServiceTypeForCpt(cptCode, serviceTypes);
  return {
    ...row,
    code: normalizeCpt(cptCode),
    healthcareServiceId: serviceType?.id ?? row.healthcareServiceId,
    display: serviceType?.billingTitle ?? row.display,
    units: units ?? row.units ?? 1,
  };
}

/**
 * Apply a single accepted scrubber result to note metadata billing rows.
 */
export function applyAcceptedScrubberResult(
  metadata: NoteMetadataSnapshot,
  result: EncounterScrubberResult,
  serviceTypes: BillingServiceTypeRef[] = [],
): NoteMetadataSnapshot {
  const code = normalizeCpt(result.code);
  let rows = billingRows(metadata);

  switch (result.action) {
    case 'suggest_replace': {
      if (!result.suggestedCode) return metadata;
      const suggested = normalizeCpt(result.suggestedCode);
      const index = rows.findIndex((row) => normalizeCpt(row.code) === code);
      if (index < 0) return metadata;
      rows[index] = rowWithCpt(rows[index], suggested, serviceTypes, result.units);
      break;
    }
    case 'suggest_add': {
      if (!result.suggestedCode) return metadata;
      const suggested = normalizeCpt(result.suggestedCode);
      if (rows.some((row) => normalizeCpt(row.code) === suggested)) return metadata;
      const serviceType = resolveServiceTypeForCpt(suggested, serviceTypes);
      rows.push({
        code: suggested,
        units: result.units ?? 1,
        healthcareServiceId: serviceType?.id,
        display: serviceType?.billingTitle,
      });
      break;
    }
    case 'suggest_remove':
    case 'flag_unsupported': {
      rows = rows.filter((row) => normalizeCpt(row.code) !== code);
      if (rows.length === 0) rows = [defaultBillingRow()];
      break;
    }
    default:
      return metadata;
  }

  return { ...metadata, billingCodes: rows };
}

export function applyAcceptedScrubberResults(
  metadata: NoteMetadataSnapshot,
  results: EncounterScrubberResult[],
  decisions: Record<string, ScrubberSuggestionDecision>,
  serviceTypes: BillingServiceTypeRef[] = [],
): NoteMetadataSnapshot {
  let next = metadata;
  for (let index = 0; index < results.length; index++) {
    const result = results[index];
    const key = scrubberResultRowKey(result, index);
    if (decisions[key] !== 'accept') continue;
    next = applyAcceptedScrubberResult(next, result, serviceTypes);
  }
  return next;
}

export function buildEncounterScrubberReview(
  results: EncounterScrubberResult[],
  decisions: Record<string, ScrubberSuggestionDecision>,
): EncounterScrubberReview {
  return {
    reviewedAt: new Date().toISOString(),
    items: results.map((result, index) => ({
      code: result.code,
      action: result.action,
      // eslint-disable-next-line no-restricted-syntax -- authored validation text from our own code, not a caught error
      message: result.message,
      decision: decisions[scrubberResultRowKey(result, index)]!,
      suggestedCode: result.suggestedCode,
      units: result.units,
    })),
  };
}
